import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock, Search, Download, Loader2, LogOut, RefreshCw, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import LeadCard from '../components/admin/LeadCard';
import {
  fetchMe,
  fetchLeads,
  updateLeadStatus,
  deleteLead,
  exportLeadsCsv,
  logout as apiLogout,
} from '../lib/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = `${window.location.origin}/admin`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Admin = () => {
  const location = useLocation();
  const { toast } = useToast();
  // Auth state — null = checking, true = authed, false = not authed
  const [authState, setAuthState] = useState(location.state?.user ? 'authed' : 'checking');
  const [user, setUser] = useState(location.state?.user || null);
  const [authError, setAuthError] = useState('');

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Verify session via /auth/me on mount. Skip if AuthCallback already passed
  // the user via router state (avoids double round-trip).
  useEffect(() => {
    if (location.state?.user) return undefined;
    // If we're in the middle of an OAuth round-trip, AuthCallback handles it.
    if (window.location.hash?.includes('session_id=')) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const me = await fetchMe();
        if (!cancelled) {
          setUser(me);
          setAuthState('authed');
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setAuthError('הגישה נדחתה. הכתובת שלך אינה מורשית להיכנס למערכת.');
        }
        setAuthState('anon');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLeads();
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setAuthState('anon');
        toast({ title: 'פג תוקף ההתחברות', description: 'אנא התחברו מחדש' });
      } else {
        toast({ title: 'שגיאה בטעינה', description: 'נסו שוב' });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (authState === 'authed') refresh();
  }, [authState, refresh]);

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch {
      /* ignore — clear local state regardless */
    }
    setUser(null);
    setAuthState('anon');
    setLeads([]);
  };

  const handleStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      await refresh();
      toast({ title: 'סטטוס עודכן' });
    } catch {
      toast({ title: 'שגיאה בעדכון' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק את הליד?')) return;
    try {
      await deleteLead(id);
      await refresh();
      toast({ title: 'ליד נמחק' });
    } catch {
      toast({ title: 'שגיאה במחיקה' });
    }
  };

  const handleExport = () => {
    setExporting(true);
    try {
      exportLeadsCsv();
      toast({ title: 'הקובץ נפתח להורדה', description: 'בדקו את תיקיית ההורדות' });
    } finally {
      setTimeout(() => setExporting(false), 800);
    }
  };

  // Source mapping — friendly Hebrew labels per landing/source page
  const SOURCE_LABELS = {
    'ceo-landing': { label: 'דף נחיתה — B2B / תעשייה', color: 'bg-navy text-cream' },
    'expert-landing': { label: 'דף נחיתה — מומחים', color: 'bg-bronze/25 text-bronze' },
    'landing-diagnostic': { label: 'דף נחיתה — אבחון', color: 'bg-bronze/30 text-bronze' },
    'investor-landing': { label: 'דף נחיתה — משקיעים', color: 'bg-navy/80 text-cream' },
    'zchuta-landing': { label: 'דף נחיתה — זכותא (משקיעים)', color: 'bg-bronze text-cream' },
    'contact-form': { label: 'טופס יצירת קשר', color: 'bg-navy/15 text-navy' }
  };
  const labelForSource = (src) => {
    if (!src) return { label: 'לא ידוע', color: 'bg-navy/10 text-navy/60' };
    return SOURCE_LABELS[src] || { label: src, color: 'bg-navy/10 text-navy/70' };
  };

  // Yes/No friendly translation is now handled inside LeadCard.

  const filtered = leads.filter((l) => {
    if (filter !== 'all' && l.status !== filter) return false;
    if (sourceFilter !== 'all') {
      if (sourceFilter === 'unknown' && l.source) return false;
      if (sourceFilter !== 'unknown' && l.source !== sourceFilter) return false;
    }
    const q = search.toLowerCase();
    if (!q) return true;
    return [l.fullName, l.email, l.phone, l.company].some((v) => (v || '').toLowerCase().includes(q));
  });

  // Build dynamic list of sources actually present in data (so admin sees only relevant filters)
  const availableSources = Array.from(new Set(leads.map((l) => l.source).filter(Boolean)));

  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === 'new').length,
    contacted: leads.filter((l) => l.status === 'contacted').length,
    closed: leads.filter((l) => l.status === 'closed').length,
  };

  if (authState === 'checking') {
    return (
      <section className="min-h-screen flex items-center justify-center bg-cream-200">
        <Loader2 className="w-6 h-6 animate-spin text-navy" aria-label="טוען" />
      </section>
    );
  }

  if (authState !== 'authed') {
    return (
      <section
        className="min-h-screen flex items-center justify-center bg-cream-200 px-6"
        aria-labelledby="admin-login-heading"
      >
        <div className="max-w-md w-full bg-cream p-10 border border-navy/10">
          <div className="w-12 h-12 bg-navy text-cream flex items-center justify-center mb-6" aria-hidden="true">
            <Lock className="w-5 h-5" />
          </div>
          <h1 id="admin-login-heading" className="display-2 text-navy mb-2">כניסה למערכת לידים</h1>
          <p className="text-navy/60 text-sm mb-8">
            הכניסה למערכת מוגבלת לחשבון Google מורשה בלבד.
          </p>

          {authError && (
            <div
              role="alert"
              className="flex items-start gap-3 bg-destructive/5 border border-destructive/30 px-4 py-3 mb-6 rounded-sm"
              data-testid="admin-auth-error"
            >
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-destructive">{authError}</p>
            </div>
          )}

          <button
            type="button"
            onClick={startGoogleLogin}
            className="w-full bg-white border border-navy/20 text-navy py-3.5 px-5 text-sm font-medium hover:bg-navy hover:text-cream transition-colors inline-flex items-center justify-center gap-3 rounded-sm"
            data-testid="admin-google-signin"
          >
            <svg className="w-5 h-5" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.58 2.68-3.9 2.68-6.63z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A8.99 8.99 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.32z" />
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 8.99 8.99 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z" />
            </svg>
            <span>התחברות עם Google</span>
          </button>

          <p className="text-xs text-navy/45 mt-8 leading-relaxed">
            אם הכתובת שלך אינה מורשית — תוצג הודעת שגיאה ולא יווצר חשבון.
            לקבלת גישה פנו למנהל המערכת.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-cream-200 pt-28 pb-20">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-10">
          <div>
            <p className="eyebrow text-bronze mb-3">מערכת לידים</p>
            <h1 className="display-1 text-navy">לידים נכנסים</h1>
            {user && (
              <p className="text-xs text-navy/50 mt-2" data-testid="admin-current-user">
                מחובר: <span className="font-medium text-navy/70">{user.name || user.email}</span>
                {user.email && user.name && <span className="text-navy/40"> · {user.email}</span>}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={refresh}
              disabled={loading}
              variant="outline"
              className="border-navy/30 text-navy hover:bg-navy hover:text-cream gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              רענן
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || leads.length === 0}
              className="bg-bronze text-navy hover:bg-bronze-600 gap-2"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              ייצוא ל-CSV
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-navy text-navy hover:bg-navy hover:text-cream gap-2"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> התנתקות
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 mb-10">
          {[
            { l: 'סה״כ', v: stats.total, c: 'text-navy' },
            { l: 'חדשים', v: stats.new, c: 'text-bronze' },
            { l: 'בטיפול', v: stats.contacted, c: 'text-navy' },
            { l: 'סגורים', v: stats.closed, c: 'text-navy/50' },
          ].map((s) => (
            <div key={s.l} className="bg-cream p-6">
              <p className="eyebrow text-navy/60 mb-3">{s.l}</p>
              <p className={`num-callout ${s.c}`}>{s.v}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי שם, אימייל, טלפון, חברה"
              className="bg-cream pr-12 border-navy/15"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-cream w-full sm:w-44 border-navy/15" aria-label="סנן לפי סטטוס">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">כל הסטטוסים</SelectItem>
              <SelectItem value="new">חדשים</SelectItem>
              <SelectItem value="contacted">בטיפול</SelectItem>
              <SelectItem value="closed">סגורים</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="bg-cream w-full sm:w-56 border-navy/15" aria-label="סנן לפי דף נחיתה / מקור">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">כל המקורות</SelectItem>
              {availableSources.map((src) => (
                <SelectItem key={src} value={src}>{labelForSource(src).label}</SelectItem>
              ))}
              {leads.some((l) => !l.source) && (
                <SelectItem value="unknown">לא ידוע (ישנים)</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Leads */}
        <div className="space-y-3">
          {(() => {
            if (loading) {
              return (
                <div className="bg-cream p-16 text-center text-navy/50">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </div>
              );
            }
            if (filtered.length === 0) {
              const emptyTitle = leads.length === 0 ? 'אין לידים להצגה' : 'אין תוצאות לחיפוש';
              return (
                <div className="bg-cream p-16 text-center text-navy/50">
                  <p className="font-serif text-2xl">{emptyTitle}</p>
                  <p className="text-sm mt-2">לידים שיגיעו מטופס הקשר יופיעו כאן</p>
                </div>
              );
            }
            return filtered.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                srcMeta={labelForSource(lead.source)}
                isExpanded={expandedId === lead.id}
                onToggleExpand={toggleExpanded}
                onStatusChange={handleStatus}
                onDelete={handleDelete}
              />
            ));
          })()}
        </div>
      </div>
    </section>
  );
};

export default Admin;
